import * as Sentry from '@sentry/react';
import { PublicClient, WalletClient } from 'viem';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { GameStatusEnum, MintingEnum } from 'modules/Room/game-status/constants/game-status-enum';
import { useGameStatusStore } from 'modules/Room/game-status/store/game-status-store';

import {
  DEFAULT_RECEIPT_MS,
  DEFAULT_SIGNATURE_MS,
  NULL_ADDRESS,
  NULL_MOVE,
  TOTAL_WINS_TO,
} from 'shared/constants/constants';
import {
  CloserEnum,
  MovesEnum,
  RoomStatusEnum,
  RoundOutcome,
  RoundStagesEnum,
  WinnerStatusEnum,
} from 'shared/constants/shared-enums';
import { TxStatus } from 'shared/constants/types';
import { usePlayerGamesStore } from 'shared/store/playerGamesStore';
import { useUIStore } from 'shared/store/uiStore';
import { CloserRoom, PlayerType, WriteError } from 'shared/types/app-types';
import { orchestrateTx } from 'shared/utils/orchestrate-tx';
import type { SendTransactionFn } from 'shared/viem-functions/in-game';
import { closeRoom, exitRoom, makeMove, revealMove } from 'shared/viem-functions/in-game';

export interface State {
  playerA: PlayerType;
  playerB: PlayerType;
  bet: number;
  roomStatus: RoomStatusEnum;
  winsTo: number;
  roomBalance: bigint;
  closerRoom: CloserRoom;
  refetchRoom: () => Promise<any>;
  fromRoomId: bigint;
  lastRoomIdCounter: bigint;
  roomsDataIsFetching: boolean;
  roundCounter: number;
  winnerStatus: WinnerStatusEnum;
  roundStage: RoundStagesEnum;
  roundMoves: {
    playerA: { encrMove: `0x${string}`; move: MovesEnum };
    playerB: { encrMove: `0x${string}`; move: MovesEnum };
  };
  amIPlayerA: boolean;
  someoneElseGame: boolean; // not sure that we need it
  joinRoomId: bigint | null;
  refetchLastRoomId: () => Promise<any>;
  lastRoundOutcome: RoundOutcome;
  roomExpire: boolean;
  showHash: boolean;
  lastMoves: {
    round: number;
    playerAMove: MovesEnum;
    playerBMove: MovesEnum;
  };
  expire: boolean;
  isMakingMove: boolean;
  isRevealing: boolean;
  isClosingRoom: boolean;
  isSuccessfullyClosedRoom: boolean;
  isTechnicalDefeatError: boolean;
  txHash: `0x${string}` | null;
  submittedRoundRef: number | null;
  submittedKeyRef: string | null;
}

export interface Actions {
  setPlayerA: (playerA: PlayerType) => void;
  setPlayerB: (playerB: PlayerType) => void;
  setBet: (betTotal: number) => void;
  setWinsTo: (winsTo: bigint) => void;
  setRoomBalance: (currency: string) => void;
  setRefetchRoom: (refetch: () => Promise<any>) => void;
  setRoom: (arg0: {
    playerA: PlayerType;
    playerB: PlayerType;
    bet: number;
    roomStatus: RoomStatusEnum;
    roomBalance: number;
    winsTo: bigint;
    roundCounter: number;
    winnerStatus: WinnerStatusEnum;
    closerRoom: CloserRoom;
    amIPlayerA: boolean;
    someoneElseGame: boolean;
  }) => void;
  setPlayerAAddress: (address: `0x${string}`) => void;
  resetStore: () => void;
  setFromRoomId: (fromRoomId: bigint) => void;
  setLastRoomIdCounter: (lastRoomIdCounter: bigint) => void;
  setRoomsDataIsFetching: (flag: boolean) => void;
  setRoundStage: (roundStage: RoundStagesEnum) => void;
  setRoundMoves: (roundMoves: {
    playerA: { encrMove: `0x${string}`; move: MovesEnum };
    playerB: { encrMove: `0x${string}`; move: MovesEnum };
  }) => void;
  resetRoom: (address: `0x${string}`) => void;
  setJoinRoomId: (joinRoomId: bigint | null) => void;
  setRefetchLastRoomId: (refetch: () => Promise<any>) => void;
  setLastRoundOutcome: (lastRoundOutcome: RoundOutcome) => void;
  setRoomExpire: (flag: boolean) => void;
  setShowHash: (flag: boolean) => void;
  setLastMoves: (lastMoves: {
    round: number;
    playerAMove: MovesEnum;
    playerBMove: MovesEnum;
  }) => void;
  setExpire: (flag: boolean) => void;

  makeMoveAction: (params: {
    roomId: bigint;
    roundCounter: number;
    salt: string;
    move: MovesEnum;
    walletClient: WalletClient;
    publicClient: PublicClient;
    userAddress: `0x${string}`;
    isPrivyWallet: boolean;
    sendTransaction: SendTransactionFn;
  }) => Promise<void>;

  revealMoveAction: (params: {
    roomId: bigint;
    roundCounter: number;
    move: MovesEnum;
    salt: string;
    walletClient: WalletClient;
    publicClient: PublicClient;
    userAddress: `0x${string}`;
    isPrivyWallet: boolean;
    sendTransaction: SendTransactionFn;
  }) => Promise<void>;

  closeRoomAction: (params: {
    roomId: bigint;
    walletClient: WalletClient;
    publicClient: PublicClient;
    userAddress: `0x${string}`;
    chainId: number;
  }) => Promise<void>;

  exitRoomAction: (params: {
    roomId: bigint;
    walletClient: WalletClient;
    publicClient: PublicClient;
    userAddress: `0x${string}`;
    chainId: number;
  }) => Promise<void>;
}

export const initialState: State = {
  refetchRoom: () => new Promise(() => {}),
  txHash: null,
  playerA: {
    address: NULL_ADDRESS,
    wins: 0n,
  },
  playerB: {
    address: NULL_ADDRESS,
    wins: 0n,
  },
  bet: 0,
  roomBalance: 0n,
  roomStatus: RoomStatusEnum.None,
  winsTo: TOTAL_WINS_TO,
  closerRoom: { closer: CloserEnum.None, deadline: 0n },
  fromRoomId: 0n,
  lastRoomIdCounter: 0n,
  roomsDataIsFetching: true,
  roundCounter: 1,
  winnerStatus: WinnerStatusEnum.None,
  roundStage: RoundStagesEnum.None,
  roundMoves: {
    playerA: { encrMove: NULL_MOVE, move: MovesEnum.None },
    playerB: { encrMove: NULL_MOVE, move: MovesEnum.None },
  },
  amIPlayerA: true,
  someoneElseGame: false,
  joinRoomId: null,
  refetchLastRoomId: () => new Promise(() => {}),
  lastRoundOutcome: RoundOutcome.None,
  roomExpire: false,
  showHash: false,
  lastMoves: {
    round: 0,
    playerAMove: MovesEnum.None,
    playerBMove: MovesEnum.None,
  },
  expire: false,
  isMakingMove: false,
  isRevealing: false,
  isClosingRoom: false,
  isSuccessfullyClosedRoom: false,
  isTechnicalDefeatError: false,
  submittedRoundRef: null,
  submittedKeyRef: null,
};

export const useRoomStore = create<State & Actions>()(
  immer(
    devtools((set, get) => ({
      ...initialState,
      setPlayerA: (playerA) =>
        set(() => ({
          playerA,
        })),
      setPlayerB: (playerB) =>
        set(() => ({
          playerB,
        })),
      setBet: (bet) =>
        set(() => ({
          bet,
        })),
      setWinsTo: (winsTo) =>
        set(() => ({
          winsTo,
        })),
      setRoomBalance: (roomBalance) =>
        set(() => ({
          roomBalance,
        })),
      setRefetchRoom: (refetchRoom) =>
        set(() => ({
          refetchRoom,
        })),
      setRoom: (arg0) => {
        set(() => ({
          ...arg0,
        }));
      },
      setPlayerAAddress: (address) =>
        set((state) => {
          state.playerA.address = address;
        }),
      resetStore: () =>
        set((state) => ({
          ...initialState,
          playerA: {
            ...initialState.playerA,
            address: state.playerA.address,
          },
        })),
      setFromRoomId: (fromRoomId) =>
        set(() => ({
          fromRoomId,
        })),
      setLastRoomIdCounter: (lastRoomIdCounter) =>
        set(() => ({
          lastRoomIdCounter,
        })),

      setRoomsDataIsFetching: (roomsDataIsFetching) =>
        set(() => ({
          roomsDataIsFetching,
        })),
      setRoundStage: (roundStage) =>
        set(() => ({
          roundStage,
        })),
      setRoundMoves: (roundMoves) =>
        set(() => ({
          roundMoves,
        })),
      resetRoom: (address: `0x${string}`) =>
        set(() => ({
          ...initialState,
          playerA: {
            ...initialState.playerA,
            address,
          },
        })),
      setJoinRoomId: (joinRoomId) =>
        set(() => ({
          joinRoomId,
        })),
      setRefetchLastRoomId: (refetchLastRoomId) =>
        set(() => ({
          refetchLastRoomId,
        })),
      setLastRoundOutcome: (lastRoundOutcome) =>
        set(() => ({
          lastRoundOutcome,
        })),
      setRoomExpire: (flag) =>
        set(() => ({
          roomExpire: flag,
        })),
      setShowHash: (flag) =>
        set(() => ({
          showHash: flag,
        })),
      setLastMoves: (lastMoves) =>
        set(() => ({
          lastMoves,
        })),
      setExpire: (flag) =>
        set(() => ({
          expire: flag,
        })),

      makeMoveAction: async ({
        roomId,
        roundCounter,
        salt,
        move,
        walletClient,
        publicClient,
        userAddress,
        isPrivyWallet,
        sendTransaction,
      }) => {
        if (!walletClient || !publicClient) return;

        const { isMakingMove, roomStatus, roundStage } = get();

        if (
          isMakingMove ||
          roomStatus === RoomStatusEnum.Closed ||
          roundStage === RoundStagesEnum.RevealMove
        )
          return;

        const moveKey = `${roomId}${userAddress}${publicClient.chain?.id}${roundCounter}`;
        const playerGamesStore = usePlayerGamesStore.getState();

        if (!playerGamesStore.playMoves[moveKey]) {
          usePlayerGamesStore.setState({
            playMoves: {
              ...playerGamesStore.playMoves,
              [moveKey]: {
                playerAddress: userAddress,
                salt: null,
                move: MovesEnum.None,
                moveHash: null,
                revealHash: null,
                moveStatus: TxStatus.Draft,
                revealStatus: null,
              },
            },
          });
        }

        set({
          isMakingMove: true,
          txHash: null,
          submittedRoundRef: roundCounter,
          submittedKeyRef: moveKey,
        });

        useGameStatusStore.setState({
          gameStatus: GameStatusEnum.WAIT_YOUR_APPROVE,
        });

        usePlayerGamesStore.getState().setPlayMove(moveKey, {
          playerAddress: userAddress,
          salt,
          move,
          moveHash: null,
          revealHash: null,
          moveStatus: TxStatus.Draft,
          revealStatus: null,
        });

        try {
          const now = new Date();
          await orchestrateTx(
            () =>
              makeMove({
                roomId,
                roundCounter,
                salt,
                move,
                walletClient,
                publicClient,
                userAddress,
                isPrivyWallet,
                sendTransaction,
              }),
            (tx) => publicClient.waitForTransactionReceipt({ hash: tx }),
            { signatureMs: DEFAULT_SIGNATURE_MS, receiptMs: DEFAULT_RECEIPT_MS },
            {
              onTx: (tx) => {
                set({ txHash: tx });

                useGameStatusStore
                  .getState()
                  .setPlayMoveGameStatus({ error: false, reject: false });
                const updatedPlayMove = {
                  ...usePlayerGamesStore.getState().playMoves[moveKey],
                  moveHash: { hash: tx, date: now },
                  moveStatus: TxStatus.Submitted,
                };
                usePlayerGamesStore.setState({
                  playMoves: {
                    ...usePlayerGamesStore.getState().playMoves,
                    [moveKey]: updatedPlayMove,
                  },
                });

                useGameStatusStore.setState({
                  mintingLoader: MintingEnum.YOUR_MOVE,
                  mintingHash: tx,
                });
              },
              onReceipt: (receipt) => {
                set({ isMakingMove: false, txHash: null });

                if (receipt.status === 'success') {
                  useGameStatusStore.setState({
                    mintingLoader: MintingEnum.NONE,
                    mintingHash: '',
                  });

                  useRoomStore.getState().refetchRoom();
                } else {
                  console.error('Move transaction failed', receipt);
                  Sentry.captureException('Move transaction failed');
                  usePlayerGamesStore.getState().setPlayMove(moveKey, {
                    playerAddress: userAddress,
                    move: MovesEnum.None,
                    salt: '',
                    moveHash: null,
                    revealHash: null,
                    moveStatus: TxStatus.Failed,
                    revealStatus: null,
                  });

                  useGameStatusStore.setState({
                    gameStatus: GameStatusEnum.WAIT_YOUR_MOVE,
                    mintingLoader: MintingEnum.NONE,
                    mintingHash: '',
                  });
                }
              },
              onTxTimeout: () => {
                console.warn('Move transaction timed out');
                Sentry.captureException('Move transaction timed out');
                usePlayerGamesStore.getState().setPlayMove(moveKey, {
                  playerAddress: userAddress,
                  move: MovesEnum.None,
                  salt: '',
                  moveHash: null,
                  revealHash: null,
                  moveStatus: TxStatus.Failed,
                  revealStatus: null,
                });

                useGameStatusStore.setState({
                  gameStatus: GameStatusEnum.WAIT_YOUR_MOVE,
                  mintingLoader: MintingEnum.NONE,
                  mintingHash: '',
                });
              },
            },
          );
        } catch (err: unknown) {
          console.error('Error making move:', err);

          const typed = err as WriteError | undefined;
          if (typed?.cause?.code) {
            if (typed.cause.code !== 4001) {
              Sentry.captureException(err);
            }
            useGameStatusStore.getState().setPlayMoveGameStatus({
              reject: typed.cause.code === 4001,
              error: typed.cause.code !== 4001,
            });
          } else {
            useGameStatusStore.getState().setPlayMoveGameStatus({ reject: false, error: true });
          }

          usePlayerGamesStore.getState().setPlayMove(moveKey, {
            playerAddress: userAddress,
            move: MovesEnum.None,
            salt: '',
            moveHash: null,
            revealHash: null,
            moveStatus: TxStatus.Failed,
            revealStatus: null,
          });

          useGameStatusStore.setState({
            gameStatus: GameStatusEnum.WAIT_YOUR_MOVE,
            mintingLoader: MintingEnum.NONE,
            mintingHash: '',
          });
        } finally {
          set({ isMakingMove: false, txHash: null });
        }
      },

      revealMoveAction: async ({
        roomId,
        roundCounter,
        move,
        salt,
        walletClient,
        publicClient,
        userAddress,
        isPrivyWallet,
        sendTransaction,
      }) => {
        if (!walletClient || !publicClient) return;

        const { isRevealing } = get();
        if (isRevealing) return;

        const chainId = publicClient.chain?.id;
        const revealKey = `${roomId}${userAddress}${chainId}${roundCounter}`;
        const now = new Date();

        try {
          set({ isRevealing: true });
          useGameStatusStore.setState({ gameStatus: GameStatusEnum.WAIT_YOUR_APPROVE });

          await orchestrateTx(
            () =>
              revealMove({
                roomId,
                roundCounter,
                move,
                salt,
                walletClient,
                publicClient,
                userAddress,
                isPrivyWallet,
                sendTransaction,
              }),
            (tx) => publicClient.waitForTransactionReceipt({ hash: tx }),
            { signatureMs: DEFAULT_SIGNATURE_MS, receiptMs: DEFAULT_RECEIPT_MS },
            {
              onTx: (tx) => {
                set({ submittedRoundRef: roundCounter, submittedKeyRef: revealKey });

                useRoomStore.getState().refetchRoom();

                usePlayerGamesStore
                  .getState()
                  .setRevealHash(revealKey, tx, now, TxStatus.Submitted);

                useGameStatusStore.getState().setMintingLoader(MintingEnum.YOUR_REVEAL);
                useGameStatusStore.getState().setMintingHash(tx);
              },
              onReceipt: (receipt) => {
                if (receipt.status === 'success') {
                  useRoomStore.getState().refetchRoom();
                  useGameStatusStore
                    .getState()
                    .setRevealGameStatusError({ error: false, reject: false });

                  usePlayerGamesStore.getState().setPlayMove(revealKey, {
                    ...usePlayerGamesStore.getState().playMoves[revealKey],
                    revealStatus: TxStatus.Confirmed,
                  });
                } else {
                  usePlayerGamesStore
                    .getState()
                    .setRevealHash(revealKey, null, undefined, TxStatus.Failed);
                  useGameStatusStore
                    .getState()
                    .setRevealGameStatusError({ error: true, reject: false });
                }

                useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
                useGameStatusStore.getState().setMintingHash('');
                set({ isRevealing: false, submittedRoundRef: null, submittedKeyRef: null });
              },
              onTxTimeout: () => {
                console.warn('Reveal transaction timed out');
                Sentry.captureException('Reveal transaction timed out');
                useRoomStore.getState().refetchRoom();

                usePlayerGamesStore
                  .getState()
                  .setRevealHash(revealKey, null, undefined, TxStatus.Failed);

                useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
                useGameStatusStore.getState().setMintingHash('');
                useGameStatusStore
                  .getState()
                  .setRevealGameStatusError({ error: true, reject: false });

                set({ isRevealing: false, submittedRoundRef: null, submittedKeyRef: null });
              },
              onError: (err) => {
                console.error('Error revealing move', err);

                const typed = err as WriteError | undefined;
                if (typed?.cause?.code) {
                  if (typed.cause.code !== 4001) {
                    Sentry.captureException(err);
                  }
                }

                usePlayerGamesStore
                  .getState()
                  .setRevealHash(revealKey, null, undefined, TxStatus.Failed);

                useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
                useGameStatusStore.getState().setMintingHash('');
                useGameStatusStore
                  .getState()
                  .setRevealGameStatusError({ error: true, reject: false });

                set({ isRevealing: false, submittedRoundRef: null, submittedKeyRef: null });
              },
            },
          );
        } catch (err: any) {
          console.error('Error revealing move:', err);

          if (err?.cause?.code === 4001) {
            useGameStatusStore.getState().setRevealGameStatusError({ reject: true, error: false });
          } else if (err?.message?.includes('JSON RPC error')) {
            useUIStore.getState().setRpcErrorModalShow(true);
            useGameStatusStore.getState().setRevealGameStatusError({ reject: false, error: true });
            Sentry.captureException(err);
          } else {
            useGameStatusStore.getState().setRevealGameStatusError({ reject: false, error: true });
            Sentry.captureException(err);
          }

          usePlayerGamesStore.getState().setRevealHash(revealKey, null, undefined, TxStatus.Failed);

          useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
          useGameStatusStore.getState().setMintingHash('');

          set({ isRevealing: false, submittedRoundRef: null, submittedKeyRef: null });
        }
      },

      closeRoomAction: async ({
        roomId,
        walletClient,
        publicClient,
        userAddress,
        chainId,
      }: {
        roomId: bigint;
        walletClient: WalletClient;
        publicClient: PublicClient;
        userAddress: `0x${string}`;
        chainId: number;
      }) => {
        if (!walletClient || !publicClient) return;

        const { isClosingRoom } = get();
        if (isClosingRoom) return;

        try {
          set({ isClosingRoom: true });

          await orchestrateTx(
            () =>
              closeRoom({
                roomId,
                walletClient,
                publicClient,
                userAddress,
              }),
            (tx) => publicClient.waitForTransactionReceipt({ hash: tx }),
            { signatureMs: DEFAULT_SIGNATURE_MS, receiptMs: DEFAULT_RECEIPT_MS },
            {
              onTx: (tx) => {
                useGameStatusStore.getState().setMintingLoader(MintingEnum.TAKE_MONEY_BACK);
                useGameStatusStore.getState().setMintingHash(tx);

                usePlayerGamesStore.getState().setNoOneJoinHash(chainId, tx, new Date());
              },
              onReceipt: (receipt) => {
                if (receipt.status === 'success') {
                  set({ isSuccessfullyClosedRoom: true });
                  set({ isClosingRoom: false });
                  useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
                  useGameStatusStore.getState().setMintingHash('');

                  usePlayerGamesStore.getState().setNoOneJoinHash(chainId, null);
                } else {
                  console.error('CloseRoom tx failed', receipt);
                  Sentry.captureException('CloseRoom tx failed');
                  useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
                  useGameStatusStore.getState().setMintingHash('');
                  usePlayerGamesStore.getState().setNoOneJoinHash(chainId, null);
                  set({ isClosingRoom: false, isTechnicalDefeatError: true });
                }
              },
              onError: (err) => {
                console.error('Error closing room', err);
                const typed = err as WriteError | undefined;
                if (typed?.cause?.code) {
                  if (typed.cause.code !== 4001) {
                    Sentry.captureException(err);
                  }
                }
                useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
                useGameStatusStore.getState().setMintingHash('');
                usePlayerGamesStore.getState().setNoOneJoinHash(chainId, null);
                set({ isClosingRoom: false, isTechnicalDefeatError: true });
              },
            },
          );
        } catch (err) {
          console.error('CloseRoom orchestrateTx error:', err);
          const typed = err as WriteError | undefined;
          if (typed?.cause?.code) {
            if (typed.cause.code !== 4001) {
              Sentry.captureException(err);
            }
          }
          useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
          useGameStatusStore.getState().setMintingHash('');
          usePlayerGamesStore.getState().setNoOneJoinHash(chainId, null);
          set({ isClosingRoom: false, isTechnicalDefeatError: true });
        }
      },

      exitRoomAction: async ({
        roomId,
        walletClient,
        publicClient,
        userAddress,
        chainId,
      }: {
        roomId: bigint;
        walletClient: WalletClient;
        publicClient: PublicClient;
        userAddress: `0x${string}`;
        chainId: number;
      }) => {
        if (!walletClient || !publicClient) return;

        const { isClosingRoom } = get();
        if (isClosingRoom) return;

        try {
          set({ isClosingRoom: true });

          await orchestrateTx(
            () =>
              exitRoom({
                roomId,
                walletClient,
                publicClient,
                userAddress,
              }),
            (tx) => publicClient.waitForTransactionReceipt({ hash: tx }),
            { signatureMs: DEFAULT_SIGNATURE_MS, receiptMs: DEFAULT_RECEIPT_MS },
            {
              onTx: (tx) => {
                useGameStatusStore.getState().setMintingLoader(MintingEnum.TAKE_MONEY_BACK);
                useGameStatusStore.getState().setMintingHash(tx);

                usePlayerGamesStore.getState().setNoOneJoinHash(chainId, tx, new Date());
              },
              onReceipt: (receipt) => {
                if (receipt.status === 'success') {
                  set({ isSuccessfullyClosedRoom: true });
                  set({ isClosingRoom: false });
                  useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
                  useGameStatusStore.getState().setMintingHash('');

                  usePlayerGamesStore.getState().setNoOneJoinHash(chainId, null);
                } else {
                  console.error('ExitRoom tx failed', receipt);
                  Sentry.captureException('ExitRoom tx failed');
                  useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
                  useGameStatusStore.getState().setMintingHash('');
                  usePlayerGamesStore.getState().setNoOneJoinHash(chainId, null);
                  set({ isClosingRoom: false, isTechnicalDefeatError: false });
                }
              },
              onError: (err) => {
                console.error('Error exiting room', err);
                const typed = err as WriteError | undefined;
                if (typed?.cause?.code) {
                  if (typed.cause.code !== 4001) {
                    Sentry.captureException(err);
                  }
                }
                useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
                useGameStatusStore.getState().setMintingHash('');
                usePlayerGamesStore.getState().setNoOneJoinHash(chainId, null);
                set({ isClosingRoom: false, isTechnicalDefeatError: false });
              },
            },
          );
        } catch (err) {
          console.error('ExitRoom orchestrateTx error:', err);
          const typed = err as WriteError | undefined;
          if (typed?.cause?.code) {
            if (typed.cause.code !== 4001) {
              Sentry.captureException(err);
            }
          }
          useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
          useGameStatusStore.getState().setMintingHash('');
          usePlayerGamesStore.getState().setNoOneJoinHash(chainId, null);
          set({ isClosingRoom: false, isTechnicalDefeatError: false });
        }
      },
    })),
  ),
);

if (typeof window !== 'undefined' && !(window as any).__ROOM_STORE_SUBSCRIBED__) {
  (window as any).__ROOM_STORE_SUBSCRIBED__ = true;

  useRoomStore.subscribe((state) => {
    const { roundCounter, roundStage, roomStatus, submittedRoundRef, submittedKeyRef } = state;
    const { mintingLoader } = useGameStatusStore.getState();

    if (mintingLoader === MintingEnum.YOUR_REVEAL) {
      const leftRevealStage =
        roundStage === RoundStagesEnum.InitGame ||
        roundStage === RoundStagesEnum.None ||
        roundStage < RoundStagesEnum.RevealMove;

      const roundAdvanced = submittedRoundRef !== null && roundCounter > submittedRoundRef;

      if (leftRevealStage || roundAdvanced || roomStatus === RoomStatusEnum.Closed) {
        if (submittedKeyRef) {
          usePlayerGamesStore
            .getState()
            .setRevealHash(
              submittedKeyRef,
              usePlayerGamesStore.getState().playMoves?.[submittedKeyRef]?.revealHash?.hash ?? null,
              undefined,
              TxStatus.Confirmed,
            );
        }

        useGameStatusStore.getState().setMintingLoader(MintingEnum.NONE);
        useGameStatusStore.getState().setMintingHash('');
        useRoomStore.setState({
          submittedRoundRef: null,
          submittedKeyRef: null,
          isRevealing: false,
        });
      }
    }
  });
}
